// src/components/Orders.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { doc, deleteDoc } from "firebase/firestore";
import { Box, Dialog, IconButton } from "@mui/material";
import {
  Close,
  FileDownloadRounded,
  WarningAmber,
} from "@mui/icons-material";
import { useTheme } from "../ThemeContext";

import AddProject from "./AddProject";
import AddClient from "./AddClient";
import EditProject from "./EditProject";
import {
  fetchConversionRate,
  fetchCBUAEConversionRate,
} from "../utils/invoiceGenerator";
import useProjects from "../hooks/useProjects";
import {
  createInvoiceForProject,
  createInvoiceForProjects,
  exportProjectsToExcel,
} from "../services/projectActions";
import OrdersToolbar from "./orders/OrdersToolbar";
import OrdersFilterPanel from "./orders/OrdersFilterPanel";
import DeleteProjectDialog from "./orders/DeleteProjectDialog";
import InvoiceDialog from "./orders/InvoiceDialog";
import ProjectTable from "./orders/ProjectTable";
import ProjectSummaryDialog from "./orders/ProjectSummaryDialog";
import PDFPreview from "../good-UI-example/PDFPreview";
import { showCloudSaveToast } from "./CloudSaveToast";
import { db } from "../firebaseConfig";
import { isInvoiceDateSet } from "../utils/status";
import { buildProjectSummary } from "../utils/projectSummary";
import { createNoBackdropCloseHandler } from "../utils/dialogClose";
import {
  BILLING_ENTITY_OPTIONS,
  getAllowedBillingEntitiesForProjects,
  resolveBillingEntitySelection,
} from "../utils/billingEntity";

const createEmptyDateRangeInputs = () => ({
  date: { start: "", end: "" },
  invoicedDate: { start: "", end: "" },
  paidDate: { start: "", end: "" },
});

const parseRangeFilterValue = (values = []) => {
  const rangeValue = values.find(
    (value) => typeof value === "string" && value.startsWith("range:")
  );

  if (!rangeValue) {
    return { start: "", end: "" };
  }

  const [, start, end] = rangeValue.split(":");
  return {
    start: start || "",
    end: end || "",
  };
};

const Orders = ({ user }) => {
  const {
    projects,
    search,
    selectedProjects,
    filters,
    setFilters,
    availableFilterOptions,
    filterCounts,
    hasActiveFilters,
    sortColumn,
    sortOrder,
    setSortColumn,
    setSortOrder,
    setSelectedProjects,
    handleFilterChange,
    clearAllFilters,
    setIsFilterPanelOpen,
    refreshFacetCountsNow,
    handleSearchChange,
    clearSearch,
    handleCheckboxChange,
    handleInputChange,
    handleBlur: persistProjectField,
    fetchProjects,
    convertTimestampToDate,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
  } = useProjects();

  const navigate = useNavigate();
  const { theme } = useTheme();

  const [openAdd, setOpenAdd] = useState(false);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [editableCell, setEditableCell] = useState(null);
  const [editableTaskCell, setEditableTaskCell] = useState(null);
  const tableBodyRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [pdfPreview, setPdfPreview] = useState({ url: null, filename: "" });
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | saving | success | error
  const [uploadError, setUploadError] = useState("");
  const [pendingUploads, setPendingUploads] = useState(0);
  const uploadClearTimer = useRef(null);
  const pendingUploadsRef = useRef(0);

  const taskOptions = [
    "DSS",
    "BC",
    "SR",
    "PS",
    "VS",
    "IMEQ",
    "BEQ",
    "FL",
    "SS",
    "EQ",
    "LC",
    "MA",
    "RPS",
    "SV",
    "VES",
  ];

  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoiceChoice, setInvoiceChoice] = useState("Show subject");
  const [selectedCompany, setSelectedCompany] = useState("UK");
  const [invoiceAllowedCompanies, setInvoiceAllowedCompanies] = useState(
    BILLING_ENTITY_OPTIONS
  );
  const [useManualAED, setUseManualAED] = useState(false);
  const [showGBP, setShowGBP] = useState(true);
  const [exchangeRateSource, setExchangeRateSource] =
    useState("ExchangeRate-API");
  const [exchangeRateAPIRate, setExchangeRateAPIRate] = useState(null);
  const [cbuaeRate, setCbuaeRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [combinedInvoiceNo, setCombinedInvoiceNo] = useState("");

  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [summaryPlainText, setSummaryPlainText] = useState("");
  const [summaryHtmlText, setSummaryHtmlText] = useState("");
  const [summaryProjectRef, setSummaryProjectRef] = useState("");
  const [summaryFormat, setSummaryFormat] = useState("html");
  const [summaryCurrency, setSummaryCurrency] = useState("GBP");
  const [summaryContent, setSummaryContent] = useState({
    GBP: { plainText: "", htmlText: "" },
    AED: { plainText: "", htmlText: "" },
    GBP_AED: { plainText: "", htmlText: "" },
  });
  const [hasCopiedSummary, setHasCopiedSummary] = useState(false);
  const [summaryStageText, setSummaryStageText] = useState("");
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const summaryTimersRef = useRef([]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportIncludeTasks, setExportIncludeTasks] = useState(true);
  const [showDateWarningDialog, setShowDateWarningDialog] = useState(false);

  // Filter panel state
  const [showFilters, setShowFilters] = useState(false);
  const wasFiltersOpenRef = useRef(false);
  const [activeFilterTab, setActiveFilterTab] = useState("date");
  const [dateRangeInputs, setDateRangeInputs] = useState(
    createEmptyDateRangeInputs
  );

  useEffect(() => {
    if (selectedCompany === "UK") {
      setUseManualAED(false);
      setShowGBP(true);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany === "UAE" && isInvoiceDialogOpen) {
      fetchBothRates();
    }
  }, [selectedCompany, isInvoiceDialogOpen]);

  useEffect(() => {
    setDateRangeInputs({
      date: parseRangeFilterValue(filters.date),
      invoicedDate: parseRangeFilterValue(filters.invoicedDate),
      paidDate: parseRangeFilterValue(filters.paidDate),
    });
  }, [filters.date, filters.invoicedDate, filters.paidDate]);

  useEffect(() => {
    return () => {
      if (uploadClearTimer.current) {
        clearTimeout(uploadClearTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (pendingUploads <= 0) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Your invoice is still saving to the cloud. Are you sure you want to leave?";
      return event.returnValue;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [pendingUploads]);

  const fetchBothRates = async () => {
    setLoadingRates(true);
    try {
      const [apiRate, cbuae] = await Promise.all([
        fetchConversionRate("GBP", "AED"),
        fetchCBUAEConversionRate(),
      ]);
      setExchangeRateAPIRate(apiRate);
      setCbuaeRate(cbuae);
    } catch (error) {
      console.error("Error fetching rates:", error);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleDelete = () => {
    if (selectedProjects.length !== 1) {
      alert("Please select exactly one project to delete");
      return;
    }
    setDeleteError("");
    setDeletePassword("");
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deletePassword !== "Superuser") {
      setDeleteError("Incorrect password");
      return;
    }

    try {
      const projectId = selectedProjects[0];
      await deleteDoc(doc(db, "projects", projectId));
      setIsDeleteDialogOpen(false);
      setSelectedProjects([]);
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      setDeleteError("Failed to delete project");
    }
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenAddClient = () => setOpenAddClient(true);
  const handleCloseAddClient = () => setOpenAddClient(false);

  const handleOpenEdit = () => {
    const selected = selectedProjects[0];
    setSelectedProjectId(selected);
    setOpenEdit(true);
  };

  const handleOpenEditForProject = (projectId) => {
    if (!projectId) return;
    setSelectedProjects([projectId]);
    setSelectedProjectId(projectId);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => setOpenEdit(false);

  const handleRowClick = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleProjectRefClick = (id) => {
    if (!id) return;
    window.open(`/project/${id}`, "_blank", "noopener,noreferrer");
  };

  const handleClientNavigate = (clientName) => {
    if (!clientName) return;
    navigate(`/clients?client=${encodeURIComponent(clientName)}`);
  };

  const handleShowInvoicePreview = useCallback((url, filename = "Invoice") => {
    if (!url) return;
    setPdfPreview({ url, filename });
  }, []);

  const handleCloseInvoicePreview = useCallback(() => {
    setPdfPreview({ url: null, filename: "" });
  }, []);

  const handleCellClick = (projectId, field, taskIndex = null) => {
    if (taskIndex === null) {
      setEditableCell({ projectId, field });
    } else {
      setEditableTaskCell({ projectId, taskIndex, field });
    }
  };

  const handleBlur = async (projectId, field, taskIndex = null) => {
    await persistProjectField(projectId, field, taskIndex);
    if (taskIndex === null) {
      setEditableCell(null);
    } else {
      setEditableTaskCell(null);
    }
  };

  const handleSort = useCallback(
    (field) => {
      if (sortColumn === field) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(field);
        setSortOrder("asc");
      }
    },
    [sortColumn, setSortColumn, setSortOrder]
  );

  const handleSelectAll = useCallback(
    (event) => {
      if (event.target.checked) {
        const allProjectIds = projects.map((p) => p.id);
        setSelectedProjects(allProjectIds);
      } else {
        setSelectedProjects([]);
      }
    },
    [projects, setSelectedProjects]
  );

  // Filter panel handlers
  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  useEffect(() => {
    if (showFilters && !wasFiltersOpenRef.current) {
      setIsFilterPanelOpen(true);
      refreshFacetCountsNow();
    } else if (!showFilters && wasFiltersOpenRef.current) {
      setIsFilterPanelOpen(false);
    }
    wasFiltersOpenRef.current = showFilters;
  }, [refreshFacetCountsNow, setIsFilterPanelOpen, showFilters]);

  const handleDateRangeInputChange = useCallback((filterKey, boundary, value) => {
    setDateRangeInputs((prev) => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [boundary]: value,
      },
    }));
  }, []);

  const applyDateRange = useCallback((filterKey) => {
    const { start, end } = dateRangeInputs[filterKey] || {};

    if (start && end) {
      setFilters((prev) => ({
        ...prev,
        [filterKey]: [`range:${start}:${end}`],
      }));
    }
  }, [dateRangeInputs, setFilters]);

  const clearDateRange = useCallback((filterKey) => {
    setDateRangeInputs((prev) => ({
      ...prev,
      [filterKey]: { start: "", end: "" },
    }));
    setFilters((prev) => ({ ...prev, [filterKey]: [] }));
  }, [setFilters]);

  const clearAllDateRanges = useCallback(() => {
    setDateRangeInputs(createEmptyDateRangeInputs());
    setFilters((prev) => ({
      ...prev,
      date: [],
      invoicedDate: [],
      paidDate: [],
    }));
  }, [setFilters]);

  const applyQuickDateRange = useCallback((filterKey, startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: [`range:${startDate}:${endDate}`],
    }));
  }, [setFilters]);

  const handleCreateInvoice = async () => {
    if (!selectedProjects.length) return;

    const selectedProjectObjects = projects.filter((p) =>
      selectedProjects.includes(p.id)
    );
    if (!selectedProjectObjects.length) return;

    const clients = new Set(
      selectedProjectObjects.map((p) => p.client).filter(Boolean)
    );

    if (clients.size > 1) {
      alert(
        "Please select projects belonging to the same client to generate a combined invoice."
      );
      return;
    }

    const allowedCompanies =
      getAllowedBillingEntitiesForProjects(selectedProjectObjects);
    const nextSelectedCompany = resolveBillingEntitySelection(
      selectedProjectObjects,
      selectedCompany
    );

    if (!allowedCompanies.length || !nextSelectedCompany) {
      alert(
        "Selected projects have conflicting billing entities. Update the project company before generating a combined invoice."
      );
      return;
    }

    setInvoiceAllowedCompanies(allowedCompanies);
    setSelectedCompany(nextSelectedCompany);
    setIsInvoiceDialogOpen(true);
    setCombinedInvoiceNo("");
  };

  const handleCreateInvoiceForProject = async (projectId) => {
    if (!projectId) return;
    const selectedProject = projects.find((project) => project.id === projectId);
    if (!selectedProject) return;

    const allowedCompanies = getAllowedBillingEntitiesForProjects([
      selectedProject,
    ]);
    const nextSelectedCompany = resolveBillingEntitySelection(
      [selectedProject],
      selectedCompany
    );

    if (!allowedCompanies.length || !nextSelectedCompany) {
      alert(
        "This project has a conflicting billing entity setup. Update the project company before invoicing."
      );
      return;
    }

    setSelectedProjects([projectId]);
    setInvoiceAllowedCompanies(allowedCompanies);
    setSelectedCompany(nextSelectedCompany);
    setIsInvoiceDialogOpen(true);
    setCombinedInvoiceNo("");
  };

  const handleInvoiceCreation = async () => {
    if (!selectedProjects.length || isGeneratingInvoice) return;
    const selectedProjectObjects = projects.filter((p) =>
      selectedProjects.includes(p.id)
    );
    const missingInvoiceDate = selectedProjectObjects.some(
      (p) => !isInvoiceDateSet(p?.dateForInvoice)
    );

    if (missingInvoiceDate) {
      setShowDateWarningDialog(true);
      return;
    }

    await proceedWithInvoiceCreation();
  };

  const handleUploadTracking = useCallback((uploadPromise) => {
    if (!uploadPromise || typeof uploadPromise.then !== "function") return;
    setUploadError("");
    setUploadStatus("saving");
    setPendingUploads((count) => {
      const next = count + 1;
      pendingUploadsRef.current = next;
      showCloudSaveToast({
        status: "saving",
        pendingCount: next,
      });
      return next;
    });

    uploadPromise
      .then(() => {
        const stillPending = pendingUploadsRef.current > 1;
        setUploadStatus(stillPending ? "saving" : "success");
        showCloudSaveToast({
          status: stillPending ? "saving" : "success",
          pendingCount: pendingUploadsRef.current,
        });
        if (uploadClearTimer.current) {
          clearTimeout(uploadClearTimer.current);
        }
        uploadClearTimer.current = setTimeout(() => {
          setUploadStatus((status) =>
            status === "success" ? "idle" : status
          );
          setUploadError("");
        }, 2200);
      })
      .catch((error) => {
        console.error("Error saving invoice to cloud:", error);
        const message =
          error?.message || "Failed to save invoice to the cloud.";
        setUploadError(message);
        setUploadStatus("error");
        showCloudSaveToast({
          status: "error",
          errorMessage: message,
          pendingCount: pendingUploadsRef.current,
        });
      })
      .finally(() => {
        setPendingUploads((count) => {
          const next = Math.max(0, count - 1);
          pendingUploadsRef.current = next;
          return next;
        });
      });
  }, []);

  const proceedWithInvoiceCreation = async () => {
    const projectIdsToInvoice = selectedProjects.filter(Boolean);
    if (!projectIdsToInvoice.length) return;

    setShowDateWarningDialog(false);
    setIsGeneratingInvoice(true);
    try {
      const selectedProjectObjects = projects.filter((p) =>
        projectIdsToInvoice.includes(p.id)
      );
      const clients = new Set(
        selectedProjectObjects.map((p) => p.client).filter(Boolean)
      );
      if (clients.size > 1) {
        alert(
          "Selected projects belong to different clients. Please choose projects from the same client."
        );
        return;
      }

      if (projectIdsToInvoice.length === 1) {
        const result = await createInvoiceForProject({
          projectId: projectIdsToInvoice[0],
          invoiceChoice,
          selectedCompany,
          formatMoney,
          formatMoneyWithoutPoundSign,
          useManualAED,
          showGBP,
          exchangeRateSource,
        });
        if (result?.uploadPromise) {
          handleUploadTracking(result.uploadPromise);
        }
      } else {
        const result = await createInvoiceForProjects({
          projectIds: projectIdsToInvoice,
          invoiceChoice,
          selectedCompany,
          invoiceNoOverride: combinedInvoiceNo,
          formatMoney,
          formatMoneyWithoutPoundSign,
          useManualAED,
          showGBP,
          exchangeRateSource,
        });
        if (result?.uploadPromise) {
          handleUploadTracking(result.uploadPromise);
        }
      }
      setIsInvoiceDialogOpen(false);
      setCombinedInvoiceNo("");
    } catch (error) {
      console.error("Error generating invoice:", error);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const openExportDialog = () => {
    setExportIncludeTasks(true);
    setIsExportDialogOpen(true);
  };

  const closeExportDialog = () => {
    if (isExporting) return;
    setIsExportDialogOpen(false);
  };

  const exportToExcel = async (includeTasks) => {
    setIsExporting(true);

    try {
      await exportProjectsToExcel(selectedProjects, {
        filters,
        search,
        includeTasks,
      });
      setIsExportDialogOpen(false);
    } catch (error) {
      console.error("Error exporting projects:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const columnWidth = "130px";
  const rowHeight = "60px";
  const fontSize = "18px";
  const firstColumnPadding = "12px";
  const countryColumnPadding = "20px";
  const subjectColumnPadding = "12px";
  const emptyPlaceholder = "—";

  const dateformatter = (dateString, inputFormat = false) => {
    if (!dateString) {
      return inputFormat ? "" : emptyPlaceholder;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return inputFormat ? "" : emptyPlaceholder;
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return inputFormat ? `${year}-${month}-${day}` : `${day}-${month}-${year}`;
  };

  const formatMoney = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `£${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const formatMoneyWithoutPoundSign = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const normalizeDateValue = useCallback(
    (value) => {
      if (!value) return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
      }
      const converted = convertTimestampToDate(value);
      if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
        return converted;
      }
      const fallback = new Date(value);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    },
    [convertTimestampToDate]
  );

  const summarizeProject = useCallback(
    (project, currency = "GBP") =>
      buildProjectSummary(project, { normalizeDateValue, currency }),
    [normalizeDateValue]
  );

  const handleGenerateSummaryForProject = useCallback(
    (projectId) => {
      if (!projectId) return;
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;

      // Clear any existing timers
      summaryTimersRef.current.forEach((t) => clearTimeout(t));
      summaryTimersRef.current = [];

      setSummaryProjectRef(project.projectRef || "");
      const gbpSummary = summarizeProject(project, "GBP");
      const aedSummary = summarizeProject(project, "AED");
      const bothSummary = summarizeProject(project, "GBP_AED");
      setSummaryContent({
        GBP: gbpSummary,
        AED: aedSummary,
        GBP_AED: bothSummary,
      });
      setSummaryCurrency("GBP");
      setSummaryPlainText(gbpSummary.plainText);
      setSummaryHtmlText(gbpSummary.htmlText);
      setSummaryFormat("html");
      setHasCopiedSummary(false);
      setIsSummaryDialogOpen(true);
      setIsSummaryLoading(true);

      const stages = [
        { label: "Accessing data...", duration: 400 },
        { label: "Assembling information...", duration: 850 },
        { label: "Preparing draft...", duration: 600 },
        { label: "Drafting summary...", duration: 850 },
        { label: "Done", duration: 500 },
      ];

      let elapsed = 0;
      stages.forEach((stage, idx) => {
        const timer = setTimeout(() => {
          setSummaryStageText(stage.label);
          if (idx === stages.length - 1) {
            setIsSummaryLoading(false);
          }
        }, elapsed);
        summaryTimersRef.current.push(timer);
        elapsed += stage.duration;
      });
    },
    [projects, summarizeProject]
  );

  const handleOpenSummaryDialog = useCallback(() => {
    if (selectedProjects.length !== 1) return;
    const project = projects.find((p) => p.id === selectedProjects[0]);
    if (!project) return;

    // Clear any existing timers
    summaryTimersRef.current.forEach((t) => clearTimeout(t));
    summaryTimersRef.current = [];

    setSummaryProjectRef(project.projectRef || "");
    const gbpSummary = summarizeProject(project, "GBP");
    const aedSummary = summarizeProject(project, "AED");
    const bothSummary = summarizeProject(project, "GBP_AED");
    setSummaryContent({
      GBP: gbpSummary,
      AED: aedSummary,
      GBP_AED: bothSummary,
    });
    setSummaryCurrency("GBP");
    setSummaryPlainText(gbpSummary.plainText);
    setSummaryHtmlText(gbpSummary.htmlText);
    setSummaryFormat("html");
    setHasCopiedSummary(false);
    setIsSummaryDialogOpen(true);
    setIsSummaryLoading(true);

    const stages = [
      { label: "Accessing data...", duration: 400 },
      { label: "Assembling information...", duration: 850 },
      { label: "Preparing draft...", duration: 600 },
      { label: "Drafting summary...", duration: 850 },
      { label: "Done", duration: 500 },
    ];

    let elapsed = 0;
    stages.forEach((stage, idx) => {
      const timer = setTimeout(() => {
        setSummaryStageText(stage.label);
        if (idx === stages.length - 1) {
          setIsSummaryLoading(false);
        }
      }, elapsed);
      summaryTimersRef.current.push(timer);
      elapsed += stage.duration;
    });
  }, [projects, selectedProjects, summarizeProject]);

  const handleCloseSummaryDialog = useCallback(() => {
    setIsSummaryDialogOpen(false);
    summaryTimersRef.current.forEach((t) => clearTimeout(t));
    summaryTimersRef.current = [];
    setIsSummaryLoading(false);
  }, []);

  const handleSummaryFormatChange = useCallback((format) => {
    setSummaryFormat(format);
    setHasCopiedSummary(false);
  }, []);

  const handleSummaryCurrencyChange = useCallback(
    (currency) => {
      const key = (currency || "GBP").toUpperCase();
      const content =
        summaryContent[key] ||
        summaryContent.GBP || { plainText: "", htmlText: "" };
      setSummaryCurrency(key);
      setSummaryPlainText(content.plainText || "");
      setSummaryHtmlText(content.htmlText || "");
      setHasCopiedSummary(false);
    },
    [summaryContent]
  );

  const handleCopySummary = useCallback(() => {
    const markCopied = () => {
      setHasCopiedSummary(true);
      setTimeout(() => setHasCopiedSummary(false), 2000);
    };

    if (summaryFormat === "html") {
      const html = summaryHtmlText;
      const plain = summaryPlainText;
      if (!html && !plain) return;

      const fallback = () => {
        const textarea = document.createElement("textarea");
        textarea.value = html || plain;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          markCopied();
        } catch (err) {
          console.error("Failed to copy formatted summary", err);
        } finally {
          document.body.removeChild(textarea);
        }
      };

      if (navigator?.clipboard?.write) {
        const items = [
          new ClipboardItem({
            "text/html": new Blob([html || ""], { type: "text/html" }),
            "text/plain": new Blob([plain || ""], { type: "text/plain" }),
          }),
        ];
        navigator.clipboard
          .write(items)
          .then(() => markCopied())
          .catch((err) => {
            console.error("Failed to copy formatted summary", err);
            fallback();
          });
      } else {
        fallback();
      }
      return;
    }

    const textToCopy = summaryPlainText;
    if (!textToCopy) return;

    const fallbackCopy = () => {
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        markCopied();
      } catch (err) {
        console.error("Failed to copy summary", err);
      } finally {
        document.body.removeChild(textarea);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => markCopied())
        .catch((err) => {
          console.error("Failed to copy summary", err);
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  }, [summaryFormat, summaryHtmlText, summaryPlainText]);

  const handleScroll = useCallback(() => {
    const body = tableBodyRef.current;
    if (!body) return;
    if (!hasMore || isLoading || isLoadingMore) return;
    const threshold = 300;
    const position = body.scrollTop + body.clientHeight;
    if (position >= body.scrollHeight - threshold) {
      loadMore();
    }
  }, [hasMore, isLoading, isLoadingMore, loadMore]);

  useEffect(() => {
    const body = tableBodyRef.current;
    if (!body) return;
    body.addEventListener("scroll", handleScroll, { passive: true });
    return () => body.removeEventListener("scroll", handleScroll);
  }, [handleScroll, projects.length]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1500px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        overflowX: "visible",
        p: 0,
        position: "relative",
      }}
    >
      <OrdersToolbar
        search={search}
        onSearchChange={handleSearchChange}
        onClearSearch={clearSearch}
        onOpenFilterDialog={toggleFilters}
        onOpenAddProject={handleOpenAdd}
        onOpenAddClient={handleOpenAddClient}
        onOpenEdit={handleOpenEdit}
        onCreateInvoice={handleCreateInvoice}
        onDeleteProject={handleDelete}
        onExport={openExportDialog}
        onGenerateSummary={handleOpenSummaryDialog}
        selectedProjects={selectedProjects}
        user={user}
        hasActiveFilters={hasActiveFilters}
      />

      <OrdersFilterPanel
        showFilters={showFilters}
        toggleFilters={toggleFilters}
        activeFilterTab={activeFilterTab}
        handleTabChange={setActiveFilterTab}
        filters={filters}
        setFilters={setFilters}
        dateRangeInputs={dateRangeInputs}
        handleDateRangeInputChange={handleDateRangeInputChange}
        applyDateRange={applyDateRange}
        clearDateRange={clearDateRange}
        clearAllDateRanges={clearAllDateRanges}
        applyQuickDateRange={applyQuickDateRange}
        hasActiveFilters={hasActiveFilters}
        clearAllFilters={clearAllFilters}
        handleFilterChange={handleFilterChange}
        availableFilterOptions={availableFilterOptions}
        filterCounts={filterCounts}
      />

      <DeleteProjectDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        deletePassword={deletePassword}
        setDeletePassword={setDeletePassword}
        deleteError={deleteError}
        onConfirm={handleDeleteConfirm}
      />

      <InvoiceDialog
        open={isInvoiceDialogOpen}
        onClose={() => setIsInvoiceDialogOpen(false)}
        invoiceChoice={invoiceChoice}
        setInvoiceChoice={setInvoiceChoice}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        allowedCompanies={invoiceAllowedCompanies}
        useManualAED={useManualAED}
        setUseManualAED={setUseManualAED}
        showGBP={showGBP}
        setShowGBP={setShowGBP}
        exchangeRateSource={exchangeRateSource}
        setExchangeRateSource={setExchangeRateSource}
        exchangeRateAPIRate={exchangeRateAPIRate}
        cbuaeRate={cbuaeRate}
        loadingRates={loadingRates}
        isGeneratingInvoice={isGeneratingInvoice}
        onCreateInvoice={handleInvoiceCreation}
        isMultiProject={selectedProjects.length > 1}
        combinedInvoiceNo={combinedInvoiceNo}
        setCombinedInvoiceNo={setCombinedInvoiceNo}
      />

      <ProjectSummaryDialog
        open={isSummaryDialogOpen}
        onClose={handleCloseSummaryDialog}
        summaryPlainText={summaryPlainText}
        summaryHtmlText={summaryHtmlText}
        summaryFormat={summaryFormat}
        summaryCurrency={summaryCurrency}
        onFormatChange={handleSummaryFormatChange}
        onCurrencyChange={handleSummaryCurrencyChange}
        projectRef={summaryProjectRef}
        onCopy={handleCopySummary}
        hasCopied={hasCopiedSummary}
        isLoading={isSummaryLoading}
        stageText={summaryStageText}
      />

      <Dialog
        open={isExportDialogOpen}
        onClose={closeExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <div
          className={`${theme.formHeaderBg} px-6 py-4 flex items-center justify-between`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <FileDownloadRounded className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Export to Excel</h2>
              <p className={`text-sm ${theme.formSubText}`}>
                Choose the export layout
              </p>
            </div>
          </div>
          <IconButton
            onClick={closeExportDialog}
            sx={{ color: "white" }}
            className="hover:bg-white/10"
            disabled={isExporting}
          >
            <Close />
          </IconButton>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600">
            Export either one row per project or include the task breakdown for
            each project.
          </p>

          <button
            type="button"
            onClick={() => setExportIncludeTasks(false)}
            disabled={isExporting}
            className={`w-full rounded-xl border px-4 py-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              exportIncludeTasks
                ? "border-gray-200 hover:bg-gray-50"
                : "border-slate-400 bg-slate-50"
            }`}
          >
            <div className="text-sm font-semibold text-gray-900">
              Projects only
            </div>
            <div className="text-xs text-gray-500 mt-1">
              One row per project with totals, invoice ref, and VAT amounts.
            </div>
          </button>

          <button
            type="button"
            onClick={() => setExportIncludeTasks(true)}
            disabled={isExporting}
            className={`w-full rounded-xl border px-4 py-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              exportIncludeTasks
                ? "border-slate-400 bg-slate-50"
                : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            <div className="text-sm font-semibold text-gray-900">
              Projects + tasks
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Keep the current project totals and include one row per task.
            </div>
          </button>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={closeExportDialog}
            disabled={isExporting}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={() => exportToExcel(exportIncludeTasks)}
            disabled={isExporting}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-60 ${theme.toolbarExportButton}`}
          >
            {isExporting ? "Exporting..." : "Export"}
          </button>
        </div>
      </Dialog>

      <Dialog
        open={showDateWarningDialog}
        onClose={() => setShowDateWarningDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <div
          className={`${theme.formHeaderBg} px-6 py-4 flex items-center justify-between`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <WarningAmber className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Invoice Date Missing</h2>
              <p className={`text-sm ${theme.formSubText}`}>Action required</p>
            </div>
          </div>
          <IconButton
            onClick={() => setShowDateWarningDialog(false)}
            sx={{ color: "white" }}
            className="hover:bg-white/10"
          >
            <Close />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-gray-700 text-sm">
              One or more selected projects do not have an invoice date set.
              The invoice will be generated using the newest available invoice
              date and without a date for any projects that are missing one.
            </p>
          </div>
          <p className="text-gray-500 text-sm">
            Do you want to continue anyway?
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={() => setShowDateWarningDialog(false)}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={proceedWithInvoiceCreation}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all ${theme.toolbarAddButton}`}
          >
            Continue Anyway
          </button>
        </div>
      </Dialog>

      <Dialog
        open={openAdd}
        onClose={createNoBackdropCloseHandler(handleCloseAdd)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        <AddProject onClose={handleCloseAdd} />
      </Dialog>

      <Dialog
        open={openAddClient}
        onClose={createNoBackdropCloseHandler(handleCloseAddClient)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        <AddClient onClose={handleCloseAddClient} />
      </Dialog>

      <Dialog
        open={openEdit}
        onClose={createNoBackdropCloseHandler(handleCloseEdit)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        {selectedProjectId && (
          <EditProject
            projectId={selectedProjectId}
            onClose={handleCloseEdit}
          />
        )}
      </Dialog>

      <ProjectTable
        projects={projects}
        selectedProjects={selectedProjects}
        expandedRows={expandedRows}
        onToggleRow={handleRowClick}
        onToggleProject={handleCheckboxChange}
        onSelectAll={handleSelectAll}
        onProjectRefClick={handleProjectRefClick}
        onClientNavigate={handleClientNavigate}
        onEditProject={handleOpenEditForProject}
        onCreateInvoiceForProject={handleCreateInvoiceForProject}
        onGenerateSummary={handleGenerateSummaryForProject}
        onCellClick={handleCellClick}
        editableCell={editableCell}
        editableTaskCell={editableTaskCell}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        onInvoicePreview={handleShowInvoicePreview}
        taskOptions={taskOptions}
        columnWidth={columnWidth}
        rowHeight={rowHeight}
        fontSize={fontSize}
        firstColumnPadding={firstColumnPadding}
        countryColumnPadding={countryColumnPadding}
        subjectColumnPadding={subjectColumnPadding}
        emptyPlaceholder={emptyPlaceholder}
        formatMoney={formatMoney}
        dateformatter={dateformatter}
        convertTimestampToDate={convertTimestampToDate}
        bodyRef={tableBodyRef}
        loadMoreRef={loadMoreRef}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
      {pdfPreview.url && (
        <PDFPreview
          url={pdfPreview.url}
          filename={pdfPreview.filename || "Invoice"}
          onClose={handleCloseInvoicePreview}
        />
      )}
      {error && (
        <Box
          sx={{ textAlign: "center", color: "red", py: 1, fontSize: "14px" }}
        >
          {error}
        </Box>
      )}

    </Box>
  );
};

export default Orders;
